import { useState } from 'react';
import { AttendanceStatementPdf, FormattedStatementForExport } from './AttendanceStatementPdf';
import { LocalAttendanceStatement } from '../../../../types';
import { AttendanceStatement } from '../../../../types/documents';
import {
    getBreaksDurationForUploadedStatement,
    getBreaksDurationStoredStatement,
    getHoursInWorkFromStoredStatement,
    getHoursInWorkFromUploadedStatement,
    getTotalHoursFromUploadedStatement,
} from '../../../../utils/DocumentUtils';
import { roundNumber } from '../../../../utils/formatUtils';
import { pdf } from '@react-pdf/renderer';
import { useSuIntl } from '../../../../hooks/useSuIntl';
import { useGetCompanyDetail } from '../../../../query/resource-hooks/company';
import { fetchCompanyLogoAsDataUrl } from '../../../../query/apiClient';
import { useQueryClient } from 'react-query';
import CompanyService from '../../../../query/services/CompanyService';
import { CACHE_KEY_COMPANY } from '../../../../query/constants';
import { Company } from '../../../../types';

export const useDownloadDocument = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { t, formatDate, formatTime } = useSuIntl();
    const { data: company, isLoading: isCompanyLoading } = useGetCompanyDetail();
    const queryClient = useQueryClient();

    async function getCompany(): Promise<Company | undefined> {
        if (company) return company;
        return queryClient.fetchQuery(CACHE_KEY_COMPANY, CompanyService.getCompanyDetail);
    }

    function downloadBlob(blob: Blob, fileName: string) {
        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);

        // Create a temporary <a> element
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        // Programmatically trigger the download
        link.click();

        // Clean up the URL object
        URL.revokeObjectURL(url);
    }

    async function generatePdfBlob(data: LocalAttendanceStatement | AttendanceStatement): Promise<Blob> {
        const resolvedCompany = await getCompany();
        const companyLogoUrl = resolvedCompany?.logoUrl ? await fetchCompanyLogoAsDataUrl() : undefined;
        const formattedData = formatStatementDataForPdf(data, t, formatDate, formatTime, companyLogoUrl);
        return pdf(<AttendanceStatementPdf data={formattedData} />).toBlob();
    }

    async function handleDownload(data: LocalAttendanceStatement | AttendanceStatement) {
        setIsLoading(true);
        const resolvedCompany = await getCompany();
        const companyLogoUrl = resolvedCompany?.logoUrl ? await fetchCompanyLogoAsDataUrl() : undefined;
        const formattedData = formatStatementDataForPdf(data, t, formatDate, formatTime, companyLogoUrl);
        const blob = await pdf(<AttendanceStatementPdf data={formattedData} />).toBlob();
        setIsLoading(false);
        downloadBlob(blob, `${formattedData.identifier}.pdf`);
    }

    async function handlePrint(data: LocalAttendanceStatement | AttendanceStatement) {
        setIsLoading(true);
        const blob = await generatePdfBlob(data);
        setIsLoading(false);

        const url = URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);

        iframe.onload = () => {
            iframe.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(url);
            }, 1000);
        };
    }

    return {
        handleDownload,
        handlePrint,
        isLoading,
        isCompanyLoading,
    };
};

export function formatStatementDataForPdf(
    document: LocalAttendanceStatement | AttendanceStatement,
    t: (text: string) => string,
    formatDate: (date: string | undefined) => string,
    formatTime: (time: string | undefined) => string,
    companyLogoUrl?: string
): FormattedStatementForExport {
    const labels = {
        employee: t('EMPLOYEE'),
        dateFrom: t('dateFrom'),
        dateTo: t('dateTo'),
        date: t('field.date'),
        businessDays: t('attendanceStatement.duration.businessDays'),
        arrival: t('IN'),
        departure: t('OUT'),
        break: t('attendance.break'),
        workTime: t('attendance.workTime'),
        totalDays: t('attendance.daysInWork'),
        totalBreaks: t('attendance.breaks'),
        timeInWork: t('attendance.timeInWork'),
        suspendedHours: t('attendance.suspendedTime'),
        total: t('doc.total'),
        totalHours: t('attendance.timeWorked'),
        absences: t('attendanceStatement.absences'),
        type: t('type'),
    };

    const mapAbsences = (absences: typeof document extends { data: any } ? any[] | undefined : any[] | undefined) =>
        absences?.map((a) => ({
            type: t(`absence.type.${a.type}`),
            dateFrom: formatDate(a.dateFrom),
            dateTo: formatDate(a.dateTo),
            days: a.days,
        }));

    if ('userId' in document && 'type' in document && 'updatedAt' in document) {
        return {
            identifier: document.identifier,
            employeeName: document.data.data.name,
            dateFrom: formatDate(document.data.data.dateFrom),
            dateTo: formatDate(document.data.data.dateTo),
            title: t('attendanceStatement.title.long.singular'),
            companyLogoUrl,
            shifts: document?.data?.data?.reports.flatMap((report, index) =>
                report.shifts.map((shift, index) => ({
                    date: formatDate(shift.arrival?.date ?? shift.departure?.date),
                    arrival: formatTime(shift.arrival?.date),
                    departure: formatTime(shift.departure?.date),
                    break: (shift.breakDuration || 0) + 'h',
                    workTime: (shift.hoursWorked || 0) + 'h',
                }))
            ),
            absences: mapAbsences(document.data.data.absences),
            suspendedHours: document.data.data.suspendedHours?.toString() || '0',
            totalBreaks: getBreaksDurationForUploadedStatement(document) + 'h',
            timeInWork: getHoursInWorkFromUploadedStatement(document) + 'h',
            totalDays: document?.data?.data?.reports?.length || 0,
            totalHours: getTotalHoursFromUploadedStatement(document) + 'h',
            labels,
        };
    } else if ('reports' in document) {
        return {
            identifier: document.identifier,
            employeeName: document.name,
            dateFrom: formatDate(document.dateFrom),
            dateTo: formatDate(document.dateTo),
            title: t('attendanceStatement.title.long.singular'),
            companyLogoUrl,
            shifts: document?.reports.flatMap((report, index) =>
                report.shifts.map((shift, index) => ({
                    date: formatDate(shift.arrival?.date ?? shift.departure?.date),
                    arrival: formatTime(shift.arrival?.date),
                    departure: formatTime(shift.departure?.date),
                    break: (shift.breakDuration || 0) + 'h',
                    workTime: (shift.hoursWorked || 0) + 'h',
                }))
            ),
            absences: mapAbsences(document.absences),
            suspendedHours: document.suspendedHours?.toString() || '0',
            totalBreaks: getBreaksDurationStoredStatement(document) + 'h',
            timeInWork: getHoursInWorkFromStoredStatement(document) + 'h',
            totalDays: document?.reports?.length || 0,
            totalHours: roundNumber(document.totalHours ?? 0) + 'h',
            labels,
        };
    }

    throw new Error('Invalid document type');
}
